import { type BridgeSeat, DASH_COLOURS, type DashColour, type DashEvent, colourDashViewFor } from '@chessmate/game-engine';
import { CardBack } from '../components/PlayingCard.js';
import { ColourChip, DashCard, DashShape } from '../components/DashCard.js';
import { OpponentTable } from '../components/table/OpponentTable.js';
import { RoundSummaryPanel } from '../components/table/RoundSummaryPanel.js';
import { ScoreStrip } from '../components/table/ScoreStrip.js';
import { SeatPlate } from '../components/table/SeatPlate.js';
import { pointsSummary } from '../components/table/pointsSummary.js';
import { useSeatIdentity } from '../components/table/useSeatIdentity.js';
import { useT } from '../i18n/useT.js';
import { HUMAN_SEAT, type PlayerCount, useColourDashStore } from '../state/colour-dash-store.js';
import { useSeatBubbles } from '../state/table-chatter.js';

/**
 * The Colour Dash screen: you in South, robots in the other seats. It renders only from `colourDashViewFor(game, HUMAN_SEAT)`, so a hand you may
 * not see is never in the data this component can reach. Rules: match the colour, number or symbol on the pile (or play a wild and name a colour),
 * draw one card when stuck, say "last card" when you hold two, first to empty their hand wins. The deck is this app's own, with a shape for every colour.
 */
export function ColourDashScreen({ onExit }: { onExit: () => void }) {
  const { t } = useT('games');
  const game = useColourDashStore((s) => s.game);
  const result = useColourDashStore((s) => s.result);
  const totals = useColourDashStore((s) => s.totals);
  const target = useColourDashStore((s) => s.target);
  const gameWinner = useColourDashStore((s) => s.gameWinner);
  const handNumber = useColourDashStore((s) => s.handNumber);
  const seed = useColourDashStore((s) => s.seed);
  const players = useColourDashStore((s) => s.players);
  const strength = useColourDashStore((s) => s.strength);
  const pendingWild = useColourDashStore((s) => s.pendingWild);
  const tap = useColourDashStore((s) => s.tap);
  const chooseColour = useColourDashStore((s) => s.chooseColour);
  const cancelWild = useColourDashStore((s) => s.cancelWild);
  const draw = useColourDashStore((s) => s.draw);
  const pass = useColourDashStore((s) => s.pass);
  const callLast = useColourDashStore((s) => s.callLast);
  const nextHand = useColourDashStore((s) => s.nextHand);
  const newGame = useColourDashStore((s) => s.newGame);
  const setStrength = useColourDashStore((s) => s.setStrength);
  const setPlayers = useColourDashStore((s) => s.setPlayers);

  const identify = useSeatIdentity(['S', 'W', 'N', 'E'], (x) => t(`cardgame-seat-${x}`));
  const bubbles = useSeatBubbles();
  if (!game) return null;
  const view = colourDashViewFor(game, HUMAN_SEAT);
  const seatName = (s: BridgeSeat) => t(`cardgame-seat-${s}`);
  const colourName = (c: DashColour) => t(`cd-colour-${c}`);
  const legal = new Set(view.legalPlays.map((c) => c.id));
  const others = view.seats.filter((s) => s !== HUMAN_SEAT);

  let status: string;
  if (view.phase === 'done') {
    status = result
      ? result.blocked
        ? t('cd-blocked')
        : result.winner === HUMAN_SEAT
          ? t('cd-hand-won-you', { points: result.points })
          : t('cd-hand-won', { seat: seatName(result.winner), points: result.points })
      : '';
  } else if (view.canAct) {
    status = view.canPass ? t('cd-drew-playable') : view.canDraw ? t('cd-turn-draw') : t('cd-turn-you');
  } else status = t('cd-turn-wait', { seat: seatName(view.turn) });

  const eventText = (e: DashEvent): string => {
    switch (e.type) {
      case 'skipped':
        return t('cd-event-skipped', { seat: seatName(e.seat) });
      case 'reversed':
        return t('cd-event-reversed');
      case 'drew':
        return e.reason === 'last-card' ? t('cd-event-drew-last', { seat: seatName(e.seat), count: e.count }) : t('cd-event-drew', { seat: seatName(e.seat), count: e.count });
      case 'colour':
        return t('cd-event-colour', { colour: colourName(e.colour) });
    }
  };
  const events = view.events.map(eventText);

  const plate = (seat: BridgeSeat) => {
    const id = identify(seat);
    return (
      <SeatPlate
        bubble={bubbles[seat]?.line}
        testid={seat === HUMAN_SEAT ? 'cd-seat-S' : `cd-opponent-${seat}`}
        name={id.name}
        tag={id.tag}
        portrait={id.portrait}
        fallbackIcon={id.fallbackIcon}
        you={id.you}
        score={totals[seat] ?? 0}
        scoreLabel={`${seatName(seat)} ${totals[seat] ?? 0}`}
        cardsLeft={id.you ? undefined : (view.handSizes[seat] ?? 0)}
        cardsLeftLabel={t('cd-cards-left', { n: view.handSizes[seat] ?? 0 })}
        active={view.phase !== 'done' && view.turn === seat}
      />
    );
  };
  const lead = Math.max(...view.seats.map((x) => totals[x] ?? 0));
  const summary = result
    ? pointsSummary({
        round: handNumber,
        seats: view.seats,
        identify,
        totals,
        winner: result.winner,
        points: result.points,
        gameWinner,
        headline: gameWinner ? (gameWinner === HUMAN_SEAT ? t('cd-game-won') : t('cd-game-lost', { seat: seatName(gameWinner) })) : status,
      })
    : null;

  const scores = view.seats.map((s) => `${seatName(s)} ${totals[s] ?? 0}`).join(' · ');

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-4 lg:max-w-6xl lg:gap-6 lg:p-8 xl:max-w-7xl 2xl:max-w-[96rem]" data-testid="colour-dash-screen" data-appdom-scope="colour-dash">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold text-berry">{t('colour-dash-title')}</h1>
        <div className="flex items-center gap-2 text-sm font-bold">
          <span className="text-fg-muted" data-testid="cd-seed">{t('cd-deal', { n: handNumber, seed })}</span>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={newGame} data-testid="cd-new-game" data-appdom-id="cd-new-game" data-appdom-type="Button">{t('cd-new-game')}</button>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={onExit} data-testid="cd-exit" data-appdom-id="cd-exit" data-appdom-type="Button">{t('bridge-exit')}</button>
        </div>
      </header>

      <output className="min-h-[48px] glass glass-light flex flex-col gap-1 rounded-xl px-3 py-2 text-sm font-bold text-ink" data-testid="cd-status" data-appdom-id="cd-status" data-appdom-type="Text" aria-live="polite">
        <span>{status}</span>
        {events.length > 0 && <span className="font-normal text-fg-muted" data-testid="cd-events">{events.join(' · ')}</span>}
        <span className="font-normal text-fg-muted" data-testid="cd-score">{t('cd-score', { scores, target })}</span>
      </output>

      <ScoreStrip
        testid="cd-score-strip"
        title={t('table-score')}
        note={t('table-first-to', { n: target })}
        rows={view.seats.map((x) => ({ key: x, label: identify(x).name, value: totals[x] ?? 0, you: x === HUMAN_SEAT, leading: lead > 0 && (totals[x] ?? 0) === lead }))}
      />

      <OpponentTable
        opponents={others.map((x) => ({ seat: x, node: plate(x) }))}
        center={
          <div className="flex w-full flex-col items-center gap-4 lg:gap-6">
      <div className="flex flex-wrap items-center justify-center gap-6 lg:gap-12">
        <button
          type="button"
          onClick={draw}
          disabled={!view.canDraw}
          data-testid="cd-stock"
          data-appdom-id="cd-stock"
          data-appdom-type="Button"
          aria-label={`${t('cd-draw')}. ${t('cd-stock-left', { n: view.stockCount })}`}
          className="flex flex-col items-center gap-1 rounded-xl p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring enabled:hover:-translate-y-0.5 disabled:opacity-60"
        >
          <CardBack className="!w-[4.5rem] lg:!w-20" />
          <span className="text-xs font-bold text-fg-muted">{view.canDraw ? t('cd-draw') : t('cd-stock-left', { n: view.stockCount })}</span>
        </button>
        {/* biome-ignore lint/a11y/useSemanticElements: a layout cell named for screen readers; a fieldset would bring its own box styling */}
        <div className="flex flex-col items-center gap-1" data-testid="cd-discard" role="group" aria-label={t('cd-pile')}>
          <DashCard card={view.top} />
          <span className="text-xs font-bold text-fg-muted">{t('cd-pile')}</span>
        </div>
        <div className="flex flex-col items-center gap-2">
          <span data-testid="cd-match"><ColourChip colour={view.colour} label={t('cd-match', { colour: colourName(view.colour) })} /></span>
          <span className="text-xs font-bold text-fg-muted" data-testid="cd-direction" data-direction={view.direction}>
            {view.direction === 1 ? '↻ ' : '↺ '}
            {view.direction === 1 ? t('cd-direction-cw') : t('cd-direction-ccw')}
          </span>
        </div>
        {view.canPass && (
          <button type="button" onClick={pass} data-testid="cd-pass" data-appdom-id="cd-pass" data-appdom-type="Button" className="min-h-[48px] rounded-xl border border-border px-4 py-2 font-display font-semibold">
            {t('cd-pass')}
          </button>
        )}
      </div>

      {pendingWild && (
        // biome-ignore lint/a11y/useSemanticElements: a flex row of buttons; a fieldset would bring its own box styling
        <div role="group" aria-label={t('cd-pick-colour')} className="flex flex-wrap items-center justify-center gap-2" data-testid="cd-colour-choices">
          <span className="w-full text-center text-sm font-bold">{t('cd-pick-colour')}</span>
          {DASH_COLOURS.map((c) => (
            <button key={c} type="button" data-testid={`cd-pick-${c}`} onClick={() => chooseColour(c)} className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <ColourChip colour={c} label={colourName(c)} />
            </button>
          ))}
          <button type="button" onClick={cancelWild} data-testid="cd-pick-cancel" className="min-h-[48px] rounded-xl px-3 py-2 text-sm underline">
            {t('cd-cancel')}
          </button>
        </div>
      )}

          </div>
        }
        bottom={
      <section aria-label={t('bridge-hand-of', { seat: seatName(HUMAN_SEAT) })} data-testid="cd-hand" className="flex flex-col items-center gap-3">
        <div className="flex flex-col items-center gap-2">
          {plate(HUMAN_SEAT)}
          {view.canCallLast && (
            <button type="button" onClick={callLast} data-testid="cd-last-card" data-appdom-id="cd-last-card" data-appdom-type="Button" className="min-h-[48px] rounded-xl bg-accent px-3 py-1 text-sm font-display font-bold text-accent-fg">
              {t('cd-last-card')}
            </button>
          )}
          {view.calledLast && view.canAct && <span className="text-sm font-bold text-fg-muted" data-testid="cd-called">{t('cd-called')}</span>}
        </div>
        <div className="flex max-w-full flex-wrap justify-center gap-1">
          {view.hand.map((c) => (
            <DashCard key={c.id} card={c} onPlay={legal.has(c.id) ? () => tap(c) : undefined} dimmed={view.canAct && !legal.has(c.id)} />
          ))}
        </div>
      </section>

        }
      />

      {summary && (
        <RoundSummaryPanel
          testid="cd-result"
          headlineTestid="cd-game-over"
          summary={summary}
          nextLabel={gameWinner ? t('cd-new-game') : t('cd-next-hand')}
          onNext={gameWinner ? newGame : nextHand}
          nextTestid={gameWinner ? 'cd-play-again' : 'cd-next-hand'}
        />
      )}

      <details className="rounded-2xl border border-border bg-surface p-3" data-testid="cd-settings">
        <summary className="flex min-h-[48px] cursor-pointer items-center text-sm font-extrabold uppercase tracking-wide text-fg-muted">{t('bridge-robots')}</summary>
        <div className="mt-3 flex flex-col gap-3 text-sm font-bold">
          <div className="flex items-center gap-2">
            {(['easy', 'clever'] as const).map((s) => (
              <button key={s} type="button" aria-pressed={strength === s} data-testid={`cd-strength-${s}`} onClick={() => setStrength(s)} className={`min-h-[48px] rounded-lg border px-3 py-1 ${strength === s ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                {t(`bridge-${s}`)}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-1">
            <span>{t('cd-players')}</span>
            <div className="flex items-center gap-2">
              {([2, 3, 4] as PlayerCount[]).map((n) => (
                <button key={n} type="button" aria-pressed={players === n} data-testid={`cd-players-${n}`} onClick={() => setPlayers(n)} className={`min-h-[48px] rounded-lg border px-3 py-1 ${players === n ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                  {t('cd-players-n', { n })}
                </button>
              ))}
            </div>
          </div>
          <p className="flex flex-wrap items-center gap-2 font-normal text-fg-muted">
            {DASH_COLOURS.map((c) => (
              <span key={c} className="inline-flex items-center gap-1"><DashShape colour={c} />{colourName(c)}</span>
            ))}
          </p>
          <p className="font-normal text-fg-muted">{t('cd-teaching-note')}</p>
        </div>
      </details>
    </div>
  );
}
